
import { Options, Vue } from 'vue-class-component'
import BarcodeLabelList from '@/components/BarcodeLabelList.vue'
import IconRfid from '@/components/svg/IconRfid.vue'
import IconArrowBack from '@/components/svg/IconArrowBack.vue'
import RfidItemSelector from '@/views/bizBrowser/RfidItemSelector.vue'
import { ADD_TRACKING, FETCH_BARCODE, SAVE_SCANNED_BARCODES } from '@/store/actions'
import { IBarcode, IProject, ITrackingRequestBody, TError } from 'smartbarcode-web-core/src/utils/types/index'
import { tracking, getBarcode } from '@/utils/api'
import { cloneDeep } from 'lodash'
import { RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { RfidScanParsedResult } from '@/utils/bizBrowser/rfidScanResultHandler'
import errorHandler from '@/utils/errorHandler'
import { openMessage } from '@/utils/helpers'

@Options({
  components: {
    BarcodeLabelList,
    IconRfid,
    IconArrowBack,
    RfidItemSelector,
  },
  name: 'BulkTrackingRfid',
})
export default class BulkTrackingRfid extends Vue {
  loading = false
  dialogVisible = false
  checkScannedBarcode = false
  requestBody = {} as ITrackingRequestBody
  nextSelectTrackingPointName = ''
  scannedBarcodeList: IBarcode[] = []
  showRfidScanDialog = true

  get barcode(): IBarcode {
    return this.$store.state.barcode.barcode
  }

  async created() {
    if (!this.$store.state.barcode.barcode) {
      await this.$store.dispatch(FETCH_BARCODE, {
        id: this.$route.params.barcodeId,
      })
    }
    this.nextSelectTrackingPointName = this.$route.params.nextTrackName as string
    this.requestBody = JSON.parse(localStorage.tempRequestForMultipleBarcodeScan)
    this.scannedBarcodeList.push(cloneDeep(this.barcode))
  }

  goBack() {
    this.$router.back()
  }

  async beforeRouteLeave(to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) {
    if (to.name === 'scanned-barcode-list') {
      next()
      return
    }

    if (this.scannedBarcodeList.length > 1) {
      try {
        await this.$confirm(this.$t('confirm_rfid_scan_result_is_not_saved_when_leaving_page'), this.$t('warning'), {
          confirmButtonText: this.$t('ok'),
          cancelButtonText: this.$t('cancel'),
          type: 'warning',
        })
        next()
      } catch {
        next(false)
      }
    } else {
      next()
    }
  }

  get bulkUpdateOperationLimitCount() {
    const project = this.$store.state.project.details as IProject
    const projectBarcode = project.barcodeTypes?.[this.$store.getters.barcodeType]
    return projectBarcode?.bulkUpdateOperationLimitCount
  }

  onCheckScannedBarcode() {
    this.checkScannedBarcode = true
    this.dialogVisible = true
  }

  onCloseDialog() {
    this.dialogVisible = false
    this.checkScannedBarcode = false
  }

  async validateBarcode(resultScanned: IBarcode) {
    const currentProjectId = this.$store.state.project.details.id
    const scannedBCProjectId = resultScanned.projectId
    if (currentProjectId !== scannedBCProjectId) {
      throw this.$t('scanned_barcode_not_same_project')
    }

    const { barcodeType } = resultScanned
    if (!barcodeType) {
      throw this.$t('barcode not activated')
    }
    const data = {
      barcodeId: this.barcode?.id,
      barcodeIds: [resultScanned?.id],
      trackingData: this.requestBody.trackingData,
      selectedNextTrackpoint: this.nextSelectTrackingPointName,
      isDryRun: true,
    }

    await tracking(data)
  }

  async onRfidScanConfirmed(rfidList: RfidScanParsedResult[]) {
    this.loading = true
    try {
      const projectCode = this.$route?.params?.project?.toString().split('@')[0] || ''

      for (const rfidResult of rfidList) {
        try {
          const barcodeId = `projectCode=${projectCode}`
          const scannedBarcode = await getBarcode(barcodeId, rfidResult.parsedValue)

          const isAlreadyAdded = this.scannedBarcodeList.find((barcode: IBarcode) => barcode.id === scannedBarcode.id)
          if (isAlreadyAdded) {
            continue
          }

          await this.validateBarcode(scannedBarcode)
          this.scannedBarcodeList.push(scannedBarcode)
          openMessage(this.$t('barcode detected'), 'success')
        } catch (error) {
          if (error !== 'cancel') {
            errorHandler(error as TError)
          }
        }
      }
    } finally {
      this.loading = false
    }
  }

  async submit() {
    const barcodes = this.scannedBarcodeList.filter((barcode: IBarcode) => barcode.id !== this.barcode.id)
    this.$store.dispatch(SAVE_SCANNED_BARCODES, barcodes)
    this.$router.push({
      name: 'scanned-barcode-list',
      params: {
        title: this.$t('bulk_tracking_read_result'),
        operation: ADD_TRACKING,
        extParams: JSON.stringify({
          trackingData: this.requestBody.trackingData,
          selectedNextTrackpoint: this.nextSelectTrackingPointName,
        }),
      },
    })
  }
}
